import {AvatarSizes} from '@dropbox/dig-components/avatar';
import {Text} from '@dropbox/dig-components/typography';
import {Split, withShade} from '@dropbox/dig-foundations';
import cx from 'classnames';
import {Avatar} from 'components/DSYS/Avatar';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {Link} from 'components/DSYS/Link';
import {ROUTE_PATHS} from 'constant';
import {ReactNode} from 'react';
import {LinkProps} from 'react-router';

import styles from './ProfileCard.module.css';
import {ReportCountBadge} from './ReportCountBadge';

type ProfileCardAvatarSize = AvatarSizes | 'xlarge' | 'profile' | 'edit';

interface ProfileCardProps {
  profile_username?: string;
  team_id?: string;
  profile_photo_url?: string | null;
  display_name: string;
  profile_info: ReactNode;
  additional_profile_info?: string;
  size?: Exclude<AvatarSizes, 'medium' | 'standard'>;
  avatarSize?: ProfileCardAvatarSize;
  showProfileInfo?: boolean;
  displayNameClassName?: string;
  profileInfoClassName?: string;
  className?: string;
  viewOnly?: boolean;
  isDeactivated?: boolean | null;
  directReportsCount?: number | null;
  totalReportsCount?: number | null;
  source?: string;
  onClick?: () => void;
  city?: string | null;
  showEmployeeCity?: boolean;
  target?: LinkProps['target'];
}

const ProfileCard = ({
  profile_username,
  team_id,
  profile_photo_url,
  display_name,
  profile_info,
  additional_profile_info,
  source,
  size,
  avatarSize,
  showProfileInfo = true,
  displayNameClassName,
  profileInfoClassName,
  className,
  isDeactivated = false,
  viewOnly = false,
  directReportsCount = 0,
  totalReportsCount = 0,
  onClick,
  city,
  showEmployeeCity,
  target,
}: ProfileCardProps) => {
  const hasValidProfile = Boolean(profile_username && profile_username !== 'No email available');
  const profileLink = profile_username
    ? `${ROUTE_PATHS.PEOPLE}/${profile_username}`
    : team_id
      ? `${ROUTE_PATHS.TEAMS}${team_id}`
      : '';
  const hasValidLink = profile_username !== 'No email available' && Boolean(profileLink);

  const profileRole = typeof profile_info === 'string' ? profile_info : undefined;

  const linkContent = (
    <Link
      to={hasValidLink ? profileLink : '#'}
      state={{source: source ?? 'profile-card'}}
      hasNoUnderline
      isClickable={!viewOnly}
      onClick={onClick}
      target={target}
      {...withShade({
        className: cx(
          styles.profileLink,
          styles.profileContainer,
          {
            [styles.deactivatedContainer]: isDeactivated,
          },
          className
        ),
      })}
    >
      <div
        className={cx(styles.avatarContainer, {
          [styles.avatarContainerLarge]: size === 'large',
        })}
      >
        <Avatar
          key={profile_username}
          user={{name: display_name, profile_photo_url}}
          size={avatarSize ?? 'large'}
          isInactive={Boolean(isDeactivated)}
        />
        {directReportsCount && directReportsCount > 0 && totalReportsCount ? (
          <ReportCountBadge
            direct_report_count={directReportsCount}
            total_report_count={totalReportsCount}
          />
        ) : (
          <></>
        )}
      </div>
      <div className={styles.textContainer}>
        <Split alignY="center" gap="8">
          <Split.Item width="fill">
            <Text
              isBold
              className={cx(
                styles.name,
                {
                  [styles.deactivatedName]: isDeactivated,
                },
                displayNameClassName
              )}
            >
              {display_name !== ' ' ? display_name : profile_username}
            </Text>
          </Split.Item>
        </Split>
        {showEmployeeCity && city && <Text size="small">{city}</Text>}
        {showProfileInfo ? (
          <>
            <Text
              size="small"
              color="faint"
              className={cx(
                styles.name,
                styles.profileInfo,
                {
                  [styles.deactivatedName]: isDeactivated,
                },
                profileInfoClassName
              )}
            >
              {profile_info}
            </Text>
            {additional_profile_info && !isDeactivated && (
              <Text
                size="small"
                color="faint"
                isBold
                className={cx(styles.profileInfo, profileInfoClassName)}
              >
                {additional_profile_info}
              </Text>
            )}
          </>
        ) : null}
      </div>
    </Link>
  );

  if (profile_username && hasValidProfile && !viewOnly && !isDeactivated) {
    return (
      <PersonHoverCard
        ldap={profile_username}
        name={display_name}
        profilePhotoUrl={profile_photo_url}
        role={profileRole}
      >
        {linkContent}
      </PersonHoverCard>
    );
  }

  return linkContent;
};

export default ProfileCard;
